import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TSuggest } from 'feature/registration/types'

import { getSuggestQueryByUserGeo } from './actions/getSuggestQueryByUserGeo'
import { getSuggestsQueryByMapClick } from './actions/getSuggestsQueryByMapClick'

interface AddressSliceState {
  input: string
  inputSuggest: TSuggest | null
  suggests: TSuggest[]
  isLoadingSuggest: boolean
  isError: boolean
  errorMessage: string
  isValid: boolean
  isDirty: boolean
}

const getInputSuggest = () => {
  const session = sessionStorage.getItem('inputSuggest')
  return session ? JSON.parse(session) : null
}

const initialState: AddressSliceState = {
  // текущее значение input
  input: '',
  // текущее предположения адреса из дадаты
  inputSuggest: getInputSuggest(),
  // текущее предположения адреса из дадаты (полный список)
  suggests: [],
  // идет ли обращение в дадату
  isLoadingSuggest: false,
  // ошибка адреса
  isError: false,
  // сообщение ошибки адреса
  errorMessage: '',
  // валидность адреса
  isValid: false,
  // было ли тронуто поле адреса (не всегда нужна ошибка при первой загрузке)
  isDirty: false,
}

const AddressSlice = createSlice({
  name: 'address',
  initialState,
  reducers: {
    // установить текущее значение input
    setInputAddress: (state, action: PayloadAction<string>) => {
      state.input = action.payload
    },
    // установить текущее значение для inputSuggest
    setInputAddressSuggest: (state, action: PayloadAction<TSuggest>) => {
      state.inputSuggest = action.payload
      sessionStorage.setItem('inputSuggest', JSON.stringify(action.payload))
    },
    // установить текущее значение для suggests
    setSuggests: (state, action: PayloadAction<TSuggest[]>) => {
      state.suggests = action.payload
    },
    // отчистить поля адреса
    clearInputAddress: (state, _action: PayloadAction<void>) => {
      state.suggests = []
      state.inputSuggest = null
      state.input = ''
      state.isValid = false
    },
    // установить isLoadingSuggest
    setIsLoadingSuggest: (state, action: PayloadAction<boolean>) => {
      state.isLoadingSuggest = action.payload
    },
    // отчистить поля адреса включая ошибки и предположения
    clearAddress: (state, _action: PayloadAction<void>) => {
      state.input = ''
      state.inputSuggest = null
      state.isError = false
      state.errorMessage = ''
      state.isDirty = false
      state.isValid = false
      state.suggests = []
    },
    // установить валидность адреса
    setIsValidAddress: (state, action: PayloadAction<boolean>) => {
      state.isValid = action.payload
    },
    // установить IsDirty для адреса
    setIsDirtyAddress: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload
    },
    // установить isError для адреса
    setIsError: (state, action: PayloadAction<boolean>) => {
      state.isError = action.payload
    },
    // установить сообщения для ошибки адреса
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
  },
  extraReducers: builder => {
    // кликнули по карте, ставим начало запроса
    builder.addCase(getSuggestsQueryByMapClick.pending, state => {
      state.isLoadingSuggest = true
      state.isError = false
      state.errorMessage = ''
    })
    // завершен запрос после клика по карте. обрабатываем полученные данные
    builder.addCase(
      getSuggestsQueryByMapClick.fulfilled,
      (state, { payload }) => {
        state.inputSuggest = payload.data[0]
        state.isLoadingSuggest = false
        if (payload === undefined || !payload.data.length) {
          state.isError = true
          state.isValid = false
          state.input = ''
          state.inputSuggest = null
          state.errorMessage =
            'Адрес не найден. Пожалуйста, попробуйте найти адрес снова, введя запрос в поле “Адрес”'
          return state
        } else {
          state.isError = false
          state.isValid = true
          state.errorMessage = ''
        }

        state.input = payload.data[0].value
        state.suggests = payload.data
        state.inputSuggest = payload.data[0]
        return state
      },
    )
    // завершен запрос определения геолокации. обрабатываем полученные данные
    builder.addCase(
      getSuggestQueryByUserGeo.fulfilled,
      (state, { payload }) => {
        state.inputSuggest = payload.data[0]
        if (payload === undefined || !payload.data.length) {
          state.isError = true
          state.isValid = false
          state.input = ''
          state.inputSuggest = null
          state.errorMessage = 'Не удалось определить адрес'
          return
        } else {
          state.isError = false
          state.isValid = true
          state.errorMessage = ''
        }
        state.input = payload.data[0].value
        state.suggests = payload.data
      },
    )
  },
})

export const { actions, reducer } = AddressSlice
